import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

/**
 * Cart-drawer "Add to your Order" upsell component.
 *
 * Strategy:
 *   1. Read the comma-separated list of product IDs from `data-product-ids`
 *      (rendered server-side as the cart's items, in order).
 *   2. Try each in turn: fetch /recommendations/products?intent=complementary&product_id=X
 *      via the section-rendering API.
 *   3. Use the FIRST product that returns a non-empty recommendations list.
 *   4. If none of the cart items have complementary products configured, render nothing.
 *
 * On every cart update, re-evaluate the chain. The cache holds per-product responses
 * so repeated lookups are cheap.
 */
class CartRecommendations extends Component {
  /** @type {Record<string, string>} */
  #cache = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string} */
  #lastSignature = '';

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-product-ids') {
        this.#maybeLoad();
        break;
      }
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#mutationObserver.observe(this, { attributes: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, true);
    this.#maybeLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, true);
    this.#activeFetch?.abort();
  }

  /**
   * Re-evaluate when the cart drawer opens — covers the edge case where the
   * initial fetch raced with the page load or hadn't completed yet.
   * @param {Event} event
   */
  #handleDialogOpen = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target?.closest?.('cart-drawer-component')) return;
    this.#maybeLoad();
  };

  #handleCartUpdate = () => {
    // The cartUpdate event fires *before* the parent section's morphSection wipes
    // our children. Defer to a microtask so we run after the morph completes.
    queueMicrotask(() => this.#maybeLoad());
  };

  /** @returns {string[]} */
  #getProductIds() {
    const raw = this.dataset.productIds || '';
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  #maybeLoad() {
    const productIds = this.#getProductIds();
    if (productIds.length === 0) {
      this.replaceChildren();
      return;
    }

    const signature = productIds.join(',');

    // If the chain is fully cached, resolve synchronously.
    const cachedHtml = this.#firstNonEmptyFromCache(productIds);
    if (cachedHtml !== null) {
      if (this.innerHTML.trim() !== cachedHtml) {
        this.innerHTML = cachedHtml;
      }
      this.#lastSignature = signature;
      return;
    }

    // If signature unchanged and we already have content rendered, keep it.
    if (signature === this.#lastSignature && this.children.length > 0) {
      return;
    }

    this.#lastSignature = signature;
    this.#loadChain(productIds);
  }

  /**
   * Walk through cached responses; return the first non-empty one.
   * @param {string[]} productIds
   * @returns {string | null}
   */
  #firstNonEmptyFromCache(productIds) {
    let allCached = true;
    for (const productId of productIds) {
      const fullUrl = this.#urlFor(productId);
      const cached = this.#cache[fullUrl];
      if (cached === undefined) {
        allCached = false;
        continue;
      }
      if (cached.length > 0) return cached;
    }
    return allCached ? '' : null;
  }

  /**
   * @param {string} productId
   */
  #urlFor(productId) {
    return `${this.dataset.url}&product_id=${productId}`;
  }

  /**
   * Try each product id in order. Use the first that returns non-empty recs.
   * @param {string[]} productIds
   */
  async #loadChain(productIds) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const signal = this.#activeFetch.signal;

    try {
      for (const productId of productIds) {
        if (signal.aborted) return;

        const html = await this.#fetchCached(productId, signal);
        if (html === null) continue; // network/parse error — skip to next item
        if (html.length === 0) continue; // empty recs for this item — try next

        if (this.innerHTML.trim() !== html) {
          this.innerHTML = html;
        }
        return;
      }

      // None of the cart items had complementary recommendations — render nothing.
      this.replaceChildren();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Cart recommendations error:', error.message);
    } finally {
      if (this.#activeFetch?.signal === signal) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * Fetch recommendations for one product. Returns the inner HTML (may be ''),
   * or null on network error.
   * @param {string} productId
   * @param {AbortSignal} signal
   * @returns {Promise<string | null>}
   */
  async #fetchCached(productId, signal) {
    const fullUrl = this.#urlFor(productId);

    if (this.#cache[fullUrl] !== undefined) {
      return this.#cache[fullUrl];
    }

    const response = await fetch(fullUrl, { signal });
    if (!response.ok) return null;

    const text = await response.text();
    // Shopify wraps section responses in `<div id="shopify-section-...">…</div>`.
    // Strip the wrapper so we inject only the inner content.
    const parsed = new DOMParser().parseFromString(text, 'text/html');
    const wrapper = parsed.querySelector('[id^="shopify-section-"]');
    const inner = wrapper ? wrapper.innerHTML.trim() : text.trim();

    this.#cache[fullUrl] = inner;
    return inner;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
